import React, { useEffect, useState, useRef } from "react"
import Api from "../../../../_helpers/Api"
import Skeleton from "react-loading-skeleton"
import { trans } from "@/_providers/Translation"
import GroupedBarChart from "../../../partials/GroupedBarChart"
import { CHART_PALETTE } from "../../../partials/chartTheme"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import ChartInfo from "./ChartInfo"

const INITIAL_FILTERS = { from: null, to: null }

export default function CLVBySegments() {
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState(null)
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)
    const f = `${filters.from ? `?from=${filters.from || ""}` : ""}${filters.to ? `&to=${filters.to || ""}` : ""}`
    Api.get(`analytics/clv-by-segments${f}`)
      .then((res) => {
        setChartData(res.data?.chart_data ?? null)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.from || filters.to) count++
    return count
  }

  const labels = (chartData?.labels ?? []).map(String)
  const household = chartData?.household ?? []
  const corporate = chartData?.corporate ?? []
  const legend = chartData?.legend ?? [trans("analytics.households"), trans("analytics.corporate")]

  const hasData = labels.some((_, i) => (household[i] ?? 0) > 0 || (corporate[i] ?? 0) > 0)

  const series = [
    {
      name: legend[0],
      ...CHART_PALETTE[2],
      data: labels.map((_, i) => household[i] ?? 0),
    },
    {
      name: legend[1],
      ...CHART_PALETTE[0],
      data: labels.map((_, i) => corporate[i] ?? 0),
    },
  ]

  return (
    <div className="clv-by-segment-chart">
      <div className="head">
        <h3>
          {trans("analytics.clvBySegment")}
          <ChartInfo name="clvBySegments" />
        </h3>
        <div className="right">
          <ul>
            {series.map((s) => (
              <li key={s.name}>
                <span style={{
                  background: s.color2
                    ? `linear-gradient(to bottom, ${s.color1}, ${s.color2})`
                    : s.color1
                }} />
                {s.name}
              </li>
            ))}
          </ul>
          <Button onClick={(e) => { compactFilterLayoutRef.current.open(e) }}>
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? <i>{getAppliedFiltersCount()}</i> : <></>}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className='row'><div className='col'>
              <DateRange
                startDate={filters.from || ''}
                endDate={filters.to || ''}
                onStartDateChange={(date) => handleFilterChange('from', date)}
                onEndDateChange={(date) => handleFilterChange('to', date)}
                yearOnly
              />
            </div></div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={360} count={1} />
      ) : !hasData ? (
        <div className="alert warning" style={{ marginBottom: 0, width: "100%" }}>
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <GroupedBarChart labels={labels} series={series} barWidth={26} roundedBottom />
      )}
    </div>
  )
}
